import React, { useState } from 'react';
import { Search, X } from 'lucide-react';
import Card from '../../Components/Ui/Card';
import StatCard from '../../Components/Ui/StatCard';
import Table from '../../Components/Ui/Table';
import TimeFilter from '../../Components/Ui/TimeFilter';
import { productColumns, revenueY, statCards, topSellingProducts } from '../../Data/Dummydata';

const unique = (arr) => ['All', ...Array.from(new Set(arr))];

const Dashboard = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('All Brands');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [selectedSubCategory, setSelectedSubCategory] = useState('All Sub-categories');
  const [timeFilter, setTimeFilter] = useState('All Time');

  const brands = unique(topSellingProducts.map((p) => p.brand)).map((b) =>
    b === 'All' ? 'All Brands' : b,
  );
  const categories = unique(topSellingProducts.map((p) => p.category)).map((c) =>
    c === 'All' ? 'All Categories' : c,
  );
  const subCategories = unique(topSellingProducts.map((p) => p.subCategory)).map((s) =>
    s === 'All' ? 'All Sub-categories' : s,
  );
  const allMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
  const timeOptions = [
    'All Time',
    ...unique(topSellingProducts.map((p) => p.month)).filter((m) => m !== 'All'),
  ];

  const handleTimeFilterChange = (period) => {
    setTimeFilter(period);
  };

  const isMatch = (value, selected) => selected.startsWith('All') || value === selected;

  const filteredTopProducts = topSellingProducts.filter((product) => {
    return (
      isMatch(product.brand, selectedBrand) &&
      isMatch(product.category, selectedCategory) &&
      isMatch(product.subCategory, selectedSubCategory) &&
      (timeFilter === 'All Time' || product.month === timeFilter)
    );
  });

  const filteredSuggestions =
    searchQuery.length >= 3
      ? filteredTopProducts.filter((item) => {
          const searchStr =
            `${item.name} ${item.brand} ${item.category} ${item.subCategory}`.toLowerCase();
          return searchStr.includes(searchQuery.toLowerCase());
        })
      : [];

  const monthSales = {};
  const monthOrders = {};

  filteredTopProducts.forEach((product) => {
    const month = product.month;
    monthSales[month] = (monthSales[month] || 0) + product.sales;
    monthOrders[month] = (monthOrders[month] || 0) + product.orders;
  });

  const maxSales = Math.max(...Object.values(monthSales), 1);
  const maxOrders = Math.max(...Object.values(monthOrders), 1);

  const barHeights = allMonths.map((month) => ((monthSales[month] || 0) / maxSales) * 100);

  const lineYCoords = allMonths.map((month) => {
    const percent = (monthOrders[month] || 0) / maxOrders;
    return 256 - percent * 256;
  });

  const linePoints = lineYCoords.map((y, idx) => `${15 + idx * 48},${y}`).join(' ');

  return (
    <div className="space-y-6 px-4 sm:px-6 md:px-0 lg:px-0">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <h2 className="text-2xl font-bold tertiary-text-color text-center md:text-left pl-4">
          Dashboard
        </h2>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by product or category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-8 py-2 text-sm font-medium text-secondary border border-gray-300 rounded-lg w-full bg-white hover:bg-gray-50 focus:ring-2 focus:ring-primary outline-none"
            />
            {searchQuery && (
              <X
                className="absolute right-3 top-2.5 h-4 w-4 text-gray-500 cursor-pointer"
                onClick={() => setSearchQuery('')}
              />
            )}
            {searchQuery.length >= 3 && (
              <ul className="absolute z-10 mt-1 w-full max-h-48 overflow-auto bg-white border border-gray-200 rounded-md shadow-sm text-sm">
                {filteredSuggestions.length ? (
                  filteredSuggestions.map((item, idx) => (
                    <li
                      key={idx}
                      onClick={() => setSearchQuery(item.name)}
                      className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                    >
                      {item.name}
                    </li>
                  ))
                ) : (
                  <li className="px-4 py-2 text-gray-500">No results found</li>
                )}
              </ul>
            )}
          </div>

          <TimeFilter options={timeOptions} onChange={handleTimeFilterChange} />
        </div>
      </div>

      <div className="flex flex-wrap gap-3  lg:justify-end">
        <select
          value={selectedBrand}
          onChange={(e) => setSelectedBrand(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white text-secondary focus:ring-2 focus:ring-primary outline-none"
        >
          {brands.map((brand) => (
            <option key={brand} value={brand}>
              {brand}
            </option>
          ))}
        </select>

        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white text-secondary focus:ring-2 focus:ring-primary outline-none"
        >
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>

        <select
          value={selectedSubCategory}
          onChange={(e) => setSelectedSubCategory(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white text-secondary focus:ring-2 focus:ring-primary outline-none"
        >
          {subCategories.map((sub) => (
            <option key={sub} value={sub}>
              {sub}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
        {statCards.map((card, idx) => (
          <StatCard key={idx} {...card} />
        ))}
      </div>

      <Card>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
          <h2 className="xl:text-lg font-semibold text-gray-900">Sales Overview</h2>
          <div className="flex flex-wrap gap-4 text-sm text-gray-500">
            <span className="flex items-center">
              <span className="w-3 h-3 rounded-full bg-primary mr-1"></span>
              Sales
            </span>
            <span className="flex items-center">
              <span className="w-3 h-3 rounded-full bg-blue-300 mr-1"></span>
              Orders
            </span>
          </div>
        </div>

        <div className="relative w-full h-80 overflow-x-auto">
          <div className="absolute inset-0 grid grid-cols-6 grid-rows-4">
            {Array.from({ length: 24 }).map((_, idx) => (
              <div key={idx} className="border-t border-l border-gray-100" />
            ))}
          </div>

          <div className="absolute left-0 top-0 h-full flex flex-col justify-between py-2 text-xs text-gray-500 ml-2">
            {revenueY.map((label, idx) => (
              <div key={idx}>{label}</div>
            ))}
          </div>

          <div className="absolute bottom-0 left-10 right-0 flex justify-between text-xs text-gray-500 mb-1">
            {allMonths.map((month, idx) => (
              <div key={idx}>{month}</div>
            ))}
          </div>

          <div className="absolute bottom-8 left-12 right-6 flex justify-between items-end h-64">
            {barHeights.map((height, idx) => (
              <div
                key={idx}
                className="w-6 bg-primary rounded-t-sm mx-2"
                style={{ height: `${height}%` }}
              />
            ))}
          </div>

          <svg className="absolute bottom-8 left-12 right-6 h-64 overflow-visible">
            <polyline
              points={linePoints}
              fill="none"
              stroke="#93c5fd"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {lineYCoords.map((y, idx) => (
              <circle
                key={idx}
                cx={15 + idx * 48}
                cy={y}
                r="4"
                fill="white"
                stroke="#93c5fd"
                strokeWidth="2"
              />
            ))}
          </svg>
        </div>
      </Card>

      <Card title="Top Selling Products">
        <Table columns={productColumns} data={filteredTopProducts} keyExtractor={(row) => row.id} />
      </Card>
    </div>
  );
};

export default Dashboard;
